import {
  AlertCircleIcon,
  Loading03Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useState } from "react";
import { DateTime } from "luxon";
import {
  QueryClient,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, ButtonSize } from "shared/components";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { OpenReverificationRequest, ScreeningStatus } from "shared/types";
import { Application } from "types/application";
import { applicationsApi } from "api/applications";
import { getErrorMessage } from "utils/error";
import { invalidateApplicationQueries } from "shared/utils/invalidate-application-queries";
import { PartnerUserPermissions, useAuthContext } from "providers/Auth";
import {
  getReverificationApplicantName,
  getReverificationTypeLabel
} from "shared/app/ApplicationsTableView/components/ScreeningStatusBadge/reverification";
import { DashboardBanner } from "../DashboardBanner";

const ALREADY_CLOSED_ERRORS = ["not_a_rerun", "not_request_retry_yet"];

// The API finalizes `open_reverification_requests` in a background job after
// the cancel request commits, so the first refetch can still return the request
// we just canceled. We keep refetching for a few seconds until the server
// agrees that it is gone.
const SYNC_MAX_ATTEMPTS = 5;
const SYNC_RETRY_DELAY_MS = 1500;

const canCancelRequest = (
  request: OpenReverificationRequest,
  permissions: PartnerUserPermissions
) => {
  switch (request.kind) {
    case "identity_retry":
      return permissions.canEditIdv.retryFailedIdv;
    case "identity_rerun":
      return permissions.canEditIdv.rerunIdvReport;
    default:
      return permissions.canEditIncome;
  }
};

const getRequestBody = (request: OpenReverificationRequest) => {
  const date = DateTime.fromISO(request.requested_at);
  const formattedDate = date.isValid
    ? ` on ${date.toLocaleString(DateTime.DATE_MED)}`
    : "";
  const requestedBy = request.requested_by_name
    ? ` by ${request.requested_by_name}`
    : "";
  const firstName = request.applicant_first_name || "the applicant";

  return `Requested${requestedBy}${formattedDate}. The screening status will update once ${firstName} completes it.`;
};

const isSameRequest = (
  a: OpenReverificationRequest,
  b: OpenReverificationRequest
) =>
  a.kind === b.kind &&
  a.applicant_id === b.applicant_id &&
  a.verification_id === b.verification_id &&
  (a.request_id ?? null) === (b.request_id ?? null);

const hasOpenRequest = (
  application: Application | undefined,
  request: OpenReverificationRequest
) =>
  Boolean(
    application?.open_reverification_requests?.some((candidate) =>
      isSameRequest(candidate, request)
    )
  );

const removeRequestFromCache = (
  queryClient: QueryClient,
  applicationId: string,
  request: OpenReverificationRequest
) => {
  queryClient.setQueryData<Application>(
    ["APPLICATION", applicationId],
    (current) => {
      if (!current) return current;

      return {
        ...current,
        open_reverification_requests: (
          current.open_reverification_requests ?? []
        ).filter((candidate) => !isSameRequest(candidate, request))
      };
    }
  );
};

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const syncApplicationAfterCancel = async (
  queryClient: QueryClient,
  applicationId: string,
  request: OpenReverificationRequest
) => {
  const queryKey = ["APPLICATION", applicationId];

  for (let attempt = 0; attempt < SYNC_MAX_ATTEMPTS; attempt++) {
    await queryClient.invalidateQueries({ queryKey });

    const fresh = queryClient.getQueryData<Application>(queryKey);
    if (!hasOpenRequest(fresh, request)) break;

    removeRequestFromCache(queryClient, applicationId, request);
    await wait(SYNC_RETRY_DELAY_MS);
  }

  queryClient.invalidateQueries({ queryKey: ["APPLICANTS_TABLE"] });
};

const CancelReverificationRequestAction = ({
  applicationId,
  request
}: {
  applicationId: string;
  request: OpenReverificationRequest;
}) => {
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();

  const onCanceled = () => {
    toast.success("Request canceled successfully");
    setConfirming(false);
    removeRequestFromCache(queryClient, applicationId, request);
    syncApplicationAfterCancel(queryClient, applicationId, request).catch(
      () => {
        // Refetch failures surface through the application query itself.
      }
    );
  };

  const cancelRequest = useMutation({
    mutationKey: [
      "APPLICATION",
      applicationId,
      "REVERIFICATION_REQUEST",
      "DELETE"
    ],
    mutationFn: () =>
      applicationsApi.cancelOpenReverificationRequest({
        applicationId,
        request
      }),
    onSuccess: onCanceled,
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      if (ALREADY_CLOSED_ERRORS.includes(errorMessage)) {
        onCanceled();
        return;
      }
      toast.error(
        getErrorMessage(error, "Failed to cancel re-verification request")
      );
      invalidateApplicationQueries(queryClient, applicationId);
    }
  });

  return (
    <>
      <Button size={ButtonSize.XS} onClick={() => setConfirming(true)}>
        Cancel request
      </Button>

      <ConfirmDialog
        opened={confirming}
        title="Cancel this request?"
        description="You can send a new request once this one's canceled."
        variant="warning"
        icon={{ icon: AlertCircleIcon, variant: "warning" }}
        confirmButton={{
          label: "Cancel request",
          loading: cancelRequest.isPending
        }}
        hideCancelButton
        showCloseButton
        onClose={() => {
          if (cancelRequest.isPending) return;
          setConfirming(false);
        }}
        onConfirm={() => cancelRequest.mutate()}
      />
    </>
  );
};

export const ReverificationRequestBanners = ({
  application
}: {
  application: Application;
}) => {
  const { permissions } = useAuthContext();

  if (application.screening_status !== ScreeningStatus.Reverifying) return null;

  const requests = application.open_reverification_requests ?? [];
  if (!requests.length) return null;

  return (
    <div className="space-y-3">
      {requests.map((request) => (
        <DashboardBanner
          key={`${request.kind}-${request.applicant_id}-${request.verification_id}-${request.request_id ?? ""}`}
          title={`Waiting on ${getReverificationApplicantName(request)} to re-verify ${getReverificationTypeLabel(request.type)}`}
          body={getRequestBody(request)}
          icon={Loading03Icon}
          action={
            canCancelRequest(request, permissions) ? (
              <CancelReverificationRequestAction
                applicationId={String(application.id)}
                request={request}
              />
            ) : undefined
          }
        />
      ))}
    </div>
  );
};
